import { defineStore } from 'pinia';
import { useReportStore } from '@/Stores/Report';
import { useQueryStatus } from '@/Composables/useQueryStatus';
import { computed, reactive } from 'vue';
import * as GoogleMaps from '@googlemaps/js-api-loader';
import axios from 'axios';

const { Loader } = GoogleMaps;

// eslint-disable-next-line import/prefer-default-export
export const useStreetViewStore = defineStore('streetview', () => {
  const reportStore = useReportStore();

  const streetViewDataQueryStatus = useQueryStatus();

  const savingNewStreetViewPositionQueryStatus = useQueryStatus();

  type StreetViewState = {
    panoContainer: HTMLElement | null;
    panorama: google.maps.StreetViewPanorama | null;
  };

  const state = reactive<StreetViewState>({
    panoContainer: null,
    panorama: null,
  });

  // Computed
  const panorama = computed(() => state.panorama);

  // Functions
  const setPanorama = async () => {
    if (!state.panoContainer) {
      streetViewDataQueryStatus.setStatus('error');
      return;
    }

    if (!reportStore.overview.streetView?.position) {
      return;
    }

    const loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
      version: 'weekly',
    });

    await loader.load();

    state.panorama = new google.maps.StreetViewPanorama(
      state.panoContainer,
      {
        position: {
          lat: reportStore.overview.streetView?.position?.lat ?? 0,
          lng: reportStore.overview.streetView?.position?.lng ?? 0,
        },
        pov: {
          heading: reportStore.overview.streetView?.position?.heading ?? 0,
          pitch: reportStore.overview.streetView?.position?.pitch ?? 0,
        },
        visible: true,
        addressControl: false,
        panControl: false,
        zoomControl: false,
        fullscreenControl: false,
        enableCloseButton: false,
        scrollwheel: false,
      },
    );
  };

  const setPanoContainer = async (container: HTMLElement | null) => {
    if (!container) {
      return;
    }

    state.panoContainer = container;
  };

  const getPosition = () => panorama.value?.getPosition() ?? null;

  const getPov = () => panorama.value?.getPov() ?? { heading: 0, pitch: 0 };

  const reset = () => {
    panorama?.value?.setPosition({
      lat: reportStore.overview.streetView?.position?.lat ?? 0,
      lng: reportStore.overview.streetView?.position?.lng ?? 0,
    });

    panorama?.value?.setPov({
      heading: reportStore.overview.streetView?.position?.heading ?? 0,
      pitch: reportStore.overview.streetView?.position?.pitch ?? 0,
    });
  };

  const saveStreetView = async () => {
    if (!reportStore.overview.streetView) {
      streetViewDataQueryStatus.setStatus('error');
      return;
    }

    savingNewStreetViewPositionQueryStatus.setStatus('loading');

    const position: google.maps.LatLng | null = getPosition();
    const pov: google.maps.StreetViewPov = getPov();

    const streetViewData: App.Data.Report.Overview.StreetViewData = {
      display: reportStore.overview.streetView.display,
      barcode: reportStore.overview.streetView.barcode,
      url: reportStore.overview.streetView.url,
      position: {
        lat: position?.lat() ?? 0,
        lng: position?.lng() ?? 0,
        heading: pov.heading,
        pitch: pov.pitch,
      },
      overridden: true,
      order: reportStore.overview.streetView.order,
    };

    reportStore.updateStreetViewPosition(streetViewData);

    const response = await axios.put<App.Data.Report.Overview.StreetViewData>(route('internal-api.report.street-view.update', { report: reportStore.reportId }), streetViewData.position);

    if (!response.data) {
      savingNewStreetViewPositionQueryStatus.setStatus('error');
      return;
    }

    reportStore.state.content.overview.streetView.url = response.data.url;

    if (reportStore.overview.streetView?.url && reportStore.overview?.mainImageSource === 'streetview') {
      reportStore.updatePropertyImage('streetview', reportStore.overview.streetView.url);
    }

    savingNewStreetViewPositionQueryStatus.setStatus('success');

    reportStore.saveRevision();
  };

  return {
    getPosition,
    getPov,
    panorama,
    reset,
    saveStreetView,
    savingNewStreetViewPositionQueryStatus,
    setPanoContainer,
    setPanorama,
    streetViewDataQueryStatus,
  };
});
